import { styled } from '@mui/material';
import { formatDuration, intervalToDuration } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { StyledMUIButton } from 'src/components/Button';
import { SimpleSpinner } from 'src/components/Loading/SimpleSpinner';
import { Modal } from 'src/components/Modal';
import { FileUploadStatusMap } from '../../../hooks/useUploadFiles';
import { ProductFile } from '../Uploader';
import { ProgressBar } from './ProgressBar';

interface Props {
  filesUploadStatus: FileUploadStatusMap;
  close: () => void;
  isOpen: boolean;
  initNewFiles: ProductFile[];
}

export function UploadProgressModal({ filesUploadStatus, isOpen, close, initNewFiles }: Props) {
  const { t } = useTranslation('uploads');

  const uploadsStatusArray = [...filesUploadStatus.entries()];

  // This is used to show only new files loading (if you added some file, and than add some more)
  const initNewFilesNames = initNewFiles.map((file) => file.file.name);
  const newUploadStatuses = uploadsStatusArray.filter(([fileName]) => initNewFilesNames.includes(fileName));

  const isUploading = newUploadStatuses.some(
    ([_, value]) => value.isSuccess === undefined && value.isError === undefined
  );
  const successfullyUploaded = newUploadStatuses.filter(([_, value]) => value.isSuccess).length;

  const totalRemainingTime = newUploadStatuses.reduce((acc, [_, value]) => {
    if (value.remainingTime === undefined) return acc;
    /* istanbul ignore next */
    return acc + value.remainingTime;
  }, 0);

  return (
    <Modal isOpen={isOpen}>
      <ModalContent data-testid="upload-in-progress">
        <EdgeBarTop>
          {isUploading ? (
            <EdgeBarText>{t('justAMomentUploadingFiles', { count: newUploadStatuses.length })}</EdgeBarText>
          ) : (
            <EdgeBarText>{t('status.success.filesSuccessfullyUploaded', { count: successfullyUploaded })}</EdgeBarText>
          )}
        </EdgeBarTop>
        <FlexColumn>
          {newUploadStatuses.map(([fileName, value]) => (
            <ProgressBar key={fileName} name={fileName} fileUploadStatus={value} />
          ))}
        </FlexColumn>
        <EdgeBarBottom>
          {isUploading ? (
            <FlexRow style={{ alignItems: 'center' }}>
              <SimpleSpinner size="1.5em" />
              <BottomBarText>Total time left: {formatRemainingTime(totalRemainingTime)}</BottomBarText>
            </FlexRow>
          ) : (
            <CloseButton data-testid="close-progress-modal-button" onClick={close}>
              OK
            </CloseButton>
          )}
        </EdgeBarBottom>
      </ModalContent>
    </Modal>
  );
}

export function formatRemainingTime(remainingTime: number) {
  const interval = intervalToDuration({ start: 0, end: remainingTime * 1000 });
  const formattedDuration = formatDuration(interval);

  return formattedDuration;
}

const FlexColumn = styled('div')`
  display: flex;
  flex-direction: column;
`;

const FlexRow = styled('div')`
  display: flex;
`;

const CloseButton = styled(StyledMUIButton)`
  margin-left: auto;
`;

const EdgeBarText = styled('span')``;

const BottomBarText = styled(EdgeBarText)`
  margin-left: 0.5rem;
  font-size: 0.8rem;
  font-weight: bold;
`;

const EdgeBarTop = styled('div')`
  width: 100%;
  background: ${({ theme }) => theme.palette.grey['900']};
  color: ${({ theme }) => theme.palette.grey['100']};
  position: absolute;
  height: 3rem;
  top: 0;
  left: 0;
  right: 0;
  padding: 0 1.875rem;
  display: flex;
  align-items: center;
`;

const EdgeBarBottom = styled(EdgeBarTop)`
  top: unset;
  bottom: 0;
`;

const ModalContent = styled('div')`
  width: 100%;
  height: 100%;
  max-height: 300px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  margin: 2rem auto;
`;
